/**
 * HydraEffectNode - Single-input Hydra effect node
 *
 * Holds effect config (function name + param args). Chain is built
 * by HydraRenderer via graph query — no payload propagation.
 */

import { HydraBaseNode } from './HydraBaseNode';
import type { HydraFunctionMeta } from '../HydraEndpoint';
import { Dependencies } from '@/nodes/Dependencies';

export interface HydraEffectConfig {
  /** The Hydra function name (e.g., 'rotate', 'scale', 'brightness') */
  hydraFn: string;
  /** Endpoint names in order they're passed to the Hydra function */
  fnArgs: string[];
}

/**
 * Base class for single-input Hydra effect nodes.
 */
export class HydraEffectNode<T extends Record<string, any>> extends HydraBaseNode<T> {
  protected hydraFn: string = '';
  protected fnArgs: string[] = [];

  constructor(id: string, deps: Dependencies, config: HydraEffectConfig) {
    super(id, deps);
    this.hydraFn = config.hydraFn;
    this.fnArgs = config.fnArgs;
  }

  /** Return this node's metadata for graph-query chain building */
  getHydraMeta(): HydraFunctionMeta {
    return { effect: true, name: this.hydraFn, args: this.createArgGetters(this.fnArgs) };
  }
}
